/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  FetchGuestChargingProperties,
  RequestGuestChargingInterface,
  GuestChargingFilter,
  GuestChargingInitialProperties,
} from '../../../interface/partner-interfaces/guest-charging.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import {
  changeDateFormat,
  getTimeBetweenTime,
  removeEmptyKeys,
} from '../../../shared-functions/DataTime';
import FormatUtcString from '../../../shared-functions/FormatUtcString';
import guestChargingApi from '../../../utils/api/partner/guest-charging';

// a thunk to fetch stations
export const fetchGuestCharging = createAsyncThunk<
  FetchGuestChargingProperties,
  RequestGuestChargingInterface
>(
  'guest-charging/fetchGuestCharging',
  async (data: RequestGuestChargingInterface, { dispatch }) => {
    try {
      const response = await guestChargingApi.fetchGuestCharging(removeEmptyKeys({ ...data }));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to refresh guest charging
export const refreshGuestCharging = createAsyncThunk<
  FetchGuestChargingProperties,
  RequestGuestChargingInterface
>(
  'guest-charging/refreshGuestCharging',
  async (data: RequestGuestChargingInterface) => {
    const response = await guestChargingApi.fetchGuestCharging(removeEmptyKeys({ ...data }));
    return response.data;
  },
);

// a thunk to fetch stations
export const fetchGuestChargingHistory = createAsyncThunk<
  FetchGuestChargingProperties,
  { id: string }
>('guest-charging/fetchGuestChargingHistory', async ({ id }: { id: string }, { dispatch }) => {
  try {
    const response = await guestChargingApi.historyGuestCharging(id);
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'guest-charging/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await guestChargingApi.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: GuestChargingInitialProperties = {
  guestCharging: [],
  guestChargingHistory: [],
  loading: false,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const guestChargingSlice = createSlice({
  name: 'guest-charging',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setGuestChargingFilterData: (
      state: GuestChargingInitialProperties,
      action: PayloadAction<GuestChargingFilter>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state.
     */
    resetGuestChargingToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update guest charging filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateGuestFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchGuestCharging.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGuestCharging.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.guestCharging = action.payload.results?.map((stationData) => ({
          ...stationData,
          id: stationData._id,
          chargingStationNames:
            stationData?.chargingStationData
              ?.map((chargingStation) => chargingStation.name)
              ?.join(', ') || '',
          time: stationData.timeSlots.map(
            (value) =>
              `${FormatUtcString(ConvertUTCtoHHMM(value.startTime))} - ${FormatUtcString(ConvertUTCtoHHMM(value.endTime))}`,
          ),
          onSelectiveDates: stationData.onSelectiveDates.map((onSelectiveDate) =>
            changeDateFormat(ConvertUTCtoDate(onSelectiveDate)),
          ),
          fromDate: stationData?.fromDate ? ConvertUTCtoDate(stationData?.fromDate) : '',
          toDate: stationData?.toDate ? ConvertUTCtoDate(stationData?.toDate) : '',
        }));
      })
      .addCase(fetchGuestCharging.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(refreshGuestCharging.fulfilled, (state, action) => {
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
      state.guestCharging = action.payload.results?.map((stationData) => ({
        ...stationData,
        id: stationData._id,
        chargingStationNames:
          stationData?.chargingStationData
            ?.map((chargingStation) => chargingStation.name)
            ?.join(', ') || '',
        time: stationData.timeSlots.map(
          (value) =>
            `${FormatUtcString(ConvertUTCtoHHMM(value.startTime))} - ${FormatUtcString(ConvertUTCtoHHMM(value.endTime))}`,
        ),
        onSelectiveDates: stationData.onSelectiveDates.map((onSelectiveDate) =>
          changeDateFormat(ConvertUTCtoDate(onSelectiveDate)),
        ),
        fromDate: stationData?.fromDate ? ConvertUTCtoDate(stationData?.fromDate) : '',
        toDate: stationData?.toDate ? ConvertUTCtoDate(stationData?.toDate) : '',
      }));
    });
    builder
      .addCase(fetchGuestChargingHistory.pending, (state) => {
        state.loading = false;
      })
      .addCase(fetchGuestChargingHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.guestChargingHistory = action.payload.result?.map((stationData) => ({
          ...stationData,
          id: stationData._id,
          date: changeDateFormat(stationData.transactionEndsAt),
          transactionStartsAt: FormatUtcString(ConvertUTCtoHHMM(stationData.transactionStartsAt)),
          transactionEndsAt: FormatUtcString(ConvertUTCtoHHMM(stationData.transactionEndsAt)),
          duration: getTimeBetweenTime(
            stationData.transactionStartsAt,
            stationData.transactionEndsAt,
          ),
          powerConsumed: Number(stationData.meterStop) - Number(stationData.meterStart),
        }));
      })
      .addCase(fetchGuestChargingHistory.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setGuestChargingFilterData,
  resetGuestChargingToInitialState,
  updateGuestFilters,
  setSortData,
} = guestChargingSlice.actions;

// Export the reducer
export default guestChargingSlice.reducer;
